import { apiRequest } from './client'
import type { FileKey } from '@/types/bundle'
import type { ValidateDraftResult } from '@/types/diff'

interface ValidationErrorResponse {
  path: string
  message: string
}

interface FileValidationResultResponse {
  file_key: FileKey
  valid: boolean
  errors: ValidationErrorResponse[]
}

// A cross-file check's own entry (gui_backend/core/publish.py) - always
// carries file_key/path/message (the same dotted-path convention every
// other {path, message} error here uses) plus check-specific extra keys
// (source, document_type, filename, ...) this layer doesn't need.
interface CrossFileErrorResponse {
  file_key: FileKey
  path: string
  message: string
}

interface ValidateDraftResponse {
  valid: boolean
  files: FileValidationResultResponse[]
  cross_file_errors: CrossFileErrorResponse[]
}

// POST /gui/v1/validate-draft (PRD 22) - no body, validates the current
// global draft against the live bundle for any file_key it hasn't touched.
// Called via useQuery(['validate-draft']) per PRD 00's own query-key
// convention - a POST used as a query, refreshed after draft writes through
// lib/refreshAfterWrite.ts.
//
// Real incident (STATUS.md): this endpoint used to only run per-file schema
// validation, so a draft could show `valid: true` here and still fail
// Publish over one of four cross-file checks (output_resolution coverage/
// dangling-reference, prompt existence/resolution). The backend now runs
// those same checks here too, returned as a separate `cross_file_errors`
// array (each entry already carrying the file_key/path a schema error
// would). Merged into each file's own `errors[]` right here, by file_key -
// this is the *only* place that merge happens, so every downstream
// consumer (DiffValidationScreen's diff-row/jump-link matching,
// ValidationBanner's issue count) already handles it correctly with no
// further change, exactly like any other per-file validation error.
export async function validateDraft(): Promise<ValidateDraftResult> {
  const response = await apiRequest<ValidateDraftResponse>('/gui/v1/validate-draft', {
    method: 'POST',
  })
  const crossFileByKey = new Map<FileKey, ValidationErrorResponse[]>()
  for (const issue of response.cross_file_errors) {
    const existing = crossFileByKey.get(issue.file_key) ?? []
    existing.push({ path: issue.path, message: issue.message })
    crossFileByKey.set(issue.file_key, existing)
  }

  return {
    valid: response.valid,
    files: response.files.map((file) => ({
      fileKey: file.file_key,
      valid: file.valid && !crossFileByKey.has(file.file_key),
      errors: [...file.errors, ...(crossFileByKey.get(file.file_key) ?? [])],
    })),
  }
}
